// Seller Insights: classify how a shopper reached the store ("where do my visits
// come from"). Prefers a tagged share link's utm_source/src; falls back to the
// referrer host; else "direct". Referrer is passed in (not read from document)
// so the classification is pure and unit-testable.
export function deriveTrafficSource(params, referrer) {
  try {
    const utm = (params.get("utm_source") || params.get("src") || "").toLowerCase();
    const map = {
      instagram: "ig", ig: "ig", whatsapp: "wa", wa: "wa",
      facebook: "fb", fb: "fb", meta: "ad", ad: "ad",
    };
    if (utm) return map[utm] || utm;
    const ref = (referrer || "").toLowerCase();
    if (!ref) return "direct";
    if (ref.includes("instagram")) return "ig";
    if (ref.includes("whatsapp") || ref.includes("wa.me")) return "wa";
    if (ref.includes("facebook") || ref.includes("fb.")) return "fb";
    return "direct";
  } catch {
    return "direct";
  }
}

// ONE first-party cookie holding all three attribution values, so a shopper who clicks an
// ad today and buys next week still credits the campaign that brought her in.
//
// This was sessionStorage, which is per-tab and dies with the tab — so any purchase not
// finished in the same tab recorded no attribution at all. That is why orders.ord_utm_source
// is null on so many orders. utm_medium and utm_campaign were worse off still: they were read
// straight off the URL inside useRecordStoreVisit, posted to the visit endpoint once, and
// never persisted anywhere, so by checkout there was nothing left to send.
//
// 30 days matches the tools marketers compare us against: Shopify's attribution lookback and
// GA4's default acquisition lookback are both 30 days.
//
// ponytail: 30-day cookie written from JS. Safari's tracking prevention trims any JS-written
// cookie to 7 days, so iOS attribution is shorter than this claims. Fixing it needs the cookie
// set server-side via a Set-Cookie header — deliberately not done, 7 days still covers the
// large majority of purchases.
export const UTM_COOKIE = "wc_utm";
const UTM_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days, in seconds

const EMPTY_UTM = { utmSource: null, utmMedium: null, utmCampaign: null };

function readCookie(name) {
  try {
    const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(name, value) {
  try {
    // Secure only on https, so this still works on a local/dev http origin.
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${name}=${encodeURIComponent(value)}; Max-Age=${UTM_COOKIE_MAX_AGE}` +
      `; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* best-effort; attribution must never break the storefront */
  }
}

// Does this landing carry any campaign tagging at all? Drives the "non-direct" half of the
// rule below.
function isTaggedLanding(params) {
  return Boolean(
    params.get("utm_source") ||
      params.get("src") ||
      params.get("utm_medium") ||
      params.get("utm_campaign")
  );
}

/**
 * LAST NON-DIRECT CLICK — the default model in both Shopify and GA4, and the one a marketer
 * optimising ad spend wants, because it names the campaign that actually closed the sale.
 *
 * A landing that carries campaign tagging (or whose referrer identifies a real channel)
 * overwrites what is stored. A plainly direct landing NEVER overwrites, so a shopper who
 * arrives from an ad and later returns by typing the URL still credits the ad.
 *
 * This replaces the previous first-touch rule. First-touch only locked for one tab session,
 * so the difference barely showed; over a 30-day cookie it would mean an ad click four weeks
 * ago outranking the WhatsApp share she actually bought from.
 *
 * Pure (no window/document/cookie access) so it stays unit-testable.
 */
export function resolveUtm(search, referrer, stored) {
  const params = new URLSearchParams(search || "");
  const source = deriveTrafficSource(params, referrer || "");

  // Nothing to learn from this landing, and we already know where she came from.
  if (!isTaggedLanding(params) && source === "direct" && stored && stored.utmSource) {
    return stored;
  }

  return {
    utmSource: source,
    // From THIS landing only. A tagged landing with no medium/campaign genuinely has none;
    // carrying the previous landing's campaign over would credit a link she never clicked.
    utmMedium: params.get("utm_medium") || null,
    utmCampaign: params.get("utm_campaign") || null,
  };
}


// ---------------------------------------------------------------------------------------------
// Collabs: the collab code a shopper arrived with (?s=c_xxxxxxxx on a creator's collab link), so
// the order can credit the partner who brought her in. Kept in its own cookie so wc_utm keeps its
// exact shape. Same 30-day window and the same LAST NON-DIRECT CLICK rule:
//   - a landing with a collab code stores it (the window starts at the first click on that code);
//   - any other tagged or non-direct landing clears it (that click closed the sale instead);
//   - a plainly direct return keeps it.
// The server never trusts this alone: the code must belong to an active collab for THIS store.
// Only codes starting "c_" count -- other ?s= share ids are never collab codes.
export const COLLAB_COOKIE = "wc_collab";
const EMPTY_COLLAB = { collabCode: null, collabClickedAt: null };

export function resolveCollab(search, referrer, stored, now) {
  const params = new URLSearchParams(search || "");
  const s = params.get("s") || "";
  if (/^c_[a-z0-9]{6,14}$/i.test(s)) {
    if (stored && stored.collabCode === s && stored.collabClickedAt) return stored;
    return { collabCode: s, collabClickedAt: now };
  }
  const source = deriveTrafficSource(params, referrer || "");
  if (isTaggedLanding(params) || source !== "direct") return EMPTY_COLLAB;
  return stored && stored.collabCode ? stored : EMPTY_COLLAB;
}

function readStoredCollab() {
  try {
    const raw = readCookie(COLLAB_COOKIE);
    if (!raw) return EMPTY_COLLAB;
    const parsed = JSON.parse(raw);
    return {
      collabCode: parsed.collabCode || null,
      collabClickedAt: Number(parsed.collabClickedAt) || null,
    };
  } catch {
    return EMPTY_COLLAB;
  }
}

function captureCollab() {
  try {
    const stored = readStoredCollab();
    const resolved = resolveCollab(window.location.search, document.referrer, stored, Date.now());
    if (resolved.collabCode !== stored.collabCode || resolved.collabClickedAt !== stored.collabClickedAt) {
      writeCookie(COLLAB_COOKIE, JSON.stringify(resolved));
    }
  } catch {
    /* best-effort */
  }
}
// Capture attribution once per full page load, from wherever the shopper landed (home, a
// collection, a PDP). Reads window.location.search synchronously — before the SPA router can
// strip the query — and persists it for the visit and place-order calls.
export function captureUtm() {
  captureCollab();
  try {
    const stored = readStoredUtm();
    const resolved = resolveUtm(window.location.search, document.referrer, stored);
    // Write ONLY when something actually changed. Rewriting an unchanged cookie would push its
    // Max-Age 30 days further out on every page load, turning a 30-day window into a sliding
    // one — a shopper who visits monthly would credit the same ad forever. The window must run
    // from the click, which is what Shopify's 30-day lookback means.
    if (
      resolved.utmSource !== stored.utmSource ||
      resolved.utmMedium !== stored.utmMedium ||
      resolved.utmCampaign !== stored.utmCampaign
    ) {
      writeCookie(UTM_COOKIE, JSON.stringify(resolved));
    }
    return resolved;
  } catch {
    return EMPTY_UTM;
  }
}

// All three stored values (plus the collab code, if any), for the place-order calls. Always returns the full shape so a
// caller can spread it without null-checking.
export function readStoredUtm() {
  try {
    const raw = readCookie(UTM_COOKIE);
    if (!raw) return { ...EMPTY_UTM, ...readStoredCollab() };
    const parsed = JSON.parse(raw);
    return {
      utmSource: parsed.utmSource || null,
      utmMedium: parsed.utmMedium || null,
      utmCampaign: parsed.utmCampaign || null,
      ...readStoredCollab(),
    };
  } catch {
    // A malformed cookie (hand-edited, or left by an older build) must not throw on every
    // page load — treat it as no attribution.
    return EMPTY_UTM;
  }
}

// The stored source alone. Kept because the visit hook needs only this one value.
export function readStoredSource() {
  return readStoredUtm().utmSource;
}
